import React from 'react';
import classnames from 'classnames';
import { getMessage } from '@tcc/i18n';
import style from './Interval.css';
import { hexToRGB } from '../../utils/color';
import { Interval, isIntervalWithStringValue, isIntervalWithValue } from '../../types';

type RangeRenderProps = {
  interval: Interval;
  intervals: Interval[];
  index: number;
};

function renderLegendValueRange({ interval }: { interval: Interval }): string | null {
  return isIntervalWithValue(interval) ? interval.value : null;
}

function getLastRangeValue(interval: Interval, intervals: Interval[], index: number): string | undefined {
  if (!isIntervalWithValue(interval)) {
    return undefined;
  }

  const currentValue = interval.value;
  const previousInterval = intervals[index - 1];
  const previousValue = previousInterval && isIntervalWithValue(previousInterval)
    ? previousInterval.value
    : undefined;

  // Some legends include a terminal 0 bucket as a floor sentinel.
  // For display, use the prior threshold so the final bucket remains meaningful.
  if (currentValue === '0' && previousValue !== undefined) {
    return previousValue;
  }

  return currentValue;
}

function renderIntervalRange({ interval, intervals, index }: RangeRenderProps): string | null {
  if (isIntervalWithValue(interval)) {
    const previousInterval = intervals[index - 1];
    const previousValue = previousInterval && isIntervalWithValue(previousInterval)
      ? previousInterval.value
      : '';

    switch (index) {
      case 0:
        return `> ${interval.value}`;
      case intervals.length - 1:
        return `< ${getLastRangeValue(interval, intervals, index)}`;
      default:
        return `${interval.value} - ${previousValue}`;
    }
  }

  if (isIntervalWithStringValue(interval)) {
    const { stringValue } = interval;
    return getMessage(`legend.${stringValue.toLowerCase()}`) || stringValue;
  }

  return null;
}

function isNumericInterval(interval: Interval): interval is Interval & { value: string } {
  return (
    isIntervalWithValue(interval)
    && !('stringValue' in interval)
  );
}

function renderIntervalRangeAgViz({ interval, intervals, index }: RangeRenderProps): string | null {
  if (isNumericInterval(interval)) {
    const totalNumericIntervals = intervals.filter(isNumericInterval).length;
    const lastIndex = totalNumericIntervals - 1;
    const previousInterval = intervals[index - 1];
    const previousValue = previousInterval && isIntervalWithValue(previousInterval)
      ? previousInterval.value
      : '';

    switch (index) {
      case 0:
        return `> ${interval.value}`;
      case lastIndex:
        return `< ${getLastRangeValue(interval, intervals, index)}`;
      default:
        return `${interval.value} - ${previousValue}`;
    }
  }

  if (isIntervalWithStringValue(interval)) {
    const { stringValue } = interval;
    return getMessage(`legend.${stringValue.toLowerCase()}`) || stringValue;
  }

  return null;
}

interface IntervalProps {
  canExpand?: boolean;
  index: number;
  interval: Interval;
  intervals: Interval[];
  isExpanded?: boolean;
  agVizEnabled?: boolean;
}

export const IntervalComponent = ({
  index,
  interval,
  intervals,
  canExpand,
  isExpanded,
  agVizEnabled = false
}: IntervalProps) => {
  const rgbInterval = interval.hex ? hexToRGB(interval.hex) : interval;
  const background = {
    backgroundColor: `rgb(${rgbInterval.r},${rgbInterval.g},${rgbInterval.b})`,
  };
  const testId = `step-${index}`;
  const className = classnames(style.interval, {
    [style.expandable]: canExpand,
    [style.expanded]: Boolean(isExpanded),
  });
  
  const renderIntervalRangeCallback = agVizEnabled ? renderIntervalRangeAgViz : renderIntervalRange;

  const text = canExpand
    ? renderLegendValueRange({ interval })
    : renderIntervalRangeCallback({ interval, intervals, index });

  return (
    <div className={className} data-test-id={testId}>
      {text && <small className="interval-label">{text}</small>}

      {canExpand && isExpanded && <div className={style.intervalConnector} />}

      <div
        className={style.intervalColor}
        data-test-id={`color-block-${index}`}
        style={background}
      />
    </div>
  );
}
